import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import validate from 'validate.js';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Grid,
	Divider,
	Switch,
	TextField,
	Typography,
	colors,
} from '@mui/material';

import SelectField from '../../../../../../../../components/SelectField/SelectField.js';

import { SetFormState, UpdateSettings } from '../../../../../../actions/SettingsActions';

const schema = {
	firstname: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	lastname: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	operatorId: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const useStyles = makeStyles((theme) => ({
	root: {},
	saveButton: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900],
		},
	},
}));

const GeneralForm = (props) => {
	const { className, role, ...rest } = props;

	const classes = useStyles();

	const dispatch = useDispatch();

	const operators = useSelector((state) => state.settingsReducer.operators);

	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.settingsReducer.formState);

	useEffect(() => {
		const errors = validate(formState.values, schema);

		dispatch(SetFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		}));
	}, [formState.values]);

	const handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _onConfirmClick = () => {
		if (formState.isValid) {
			dispatch(UpdateSettings(formState.values));
		}
	};

	const hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	return (
		<Card {...rest} className={clsx(classes.root, className)}>
			<CardHeader title="Profile" />
			<Divider />
			<CardContent>
				<Grid container spacing={4}>
					<Grid item md={6} xs={12}>
						<TextField
							error={hasError('firstname')}
							fullWidth
							helperText={
								hasError('firstname') ? formState.errors.firstname[0] : null
							}
							label="First name"
							name="firstname"
							onChange={handleChange}
							value={formState.values.firstname || ''}
							variant="outlined"
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							error={hasError('lastname')}
							fullWidth
							helperText={
								hasError('lastname') ? formState.errors.lastname[0] : null
							}
							label="Last name"
							name="lastname"
							onChange={handleChange}
							value={formState.values.lastname || ''}
							variant="outlined"
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<TextField
							fullWidth
							label="Email Address"
							name="email"
							value={formState.values.email}
							variant="outlined"
							disabled
						/>
					</Grid>
					<Grid item md={6} xs={12}>
						<SelectField
							id="operator"
							name="operatorId"
							value={formState.values.operatorId || []}
							onChange={handleChange}
							hintText="Operator"
							menuItems={operators}
							disabled={!['Administrator', 'Manager', 'CelfinetHQ'].includes(role)}
							error={hasError('operatorId') ? formState.errors.operatorId[0] : null}
						/>
					</Grid>
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button
					className={classes.saveButton}
					onClick={_onConfirmClick}
					variant="contained"
				>
					Save Changes
				</Button>
			</CardActions>
		</Card>
	);
};

GeneralForm.propTypes = {
	className: PropTypes.string,
	role: PropTypes.string
};

export default GeneralForm;
